/**
 * App shell.
 *
 * Two different applications share this file, and the difference is
 * structural rather than cosmetic:
 *
 *   phone    one pane at a time, with the tab bar along the bottom
 *   desktop  three panes side by side — icon rail, list, conversation
 *
 * docs/references/12-desktop-three-pane.png is the desktop one. Getting this
 * wrong was the mistake that made the app look like a phone screenshot
 * stretched across a laptop: the shell used to cap everything at 520px and
 * centre it, whatever the window.
 *
 * The rail replaces the bottom tabs above 1024px. Same four destinations,
 * because they are the same app.
 */

import { Outlet, NavLink } from 'react-router-dom'
import { useChats } from '../../context/index.js'
import BottomNav from './BottomNav.jsx'
import './AppShell.css'

const RAIL = [
  {
    to: '/chats',
    label: 'Chats',
    icon: 'M5 4h14a3 3 0 0 1 3 3v8a3 3 0 0 1-3 3H9l-5 4v-4a3 3 0 0 1-3-3V7a3 3 0 0 1 3-3Z'
  },
  {
    to: '/updates',
    label: 'Updates',
    icon: null // drawn below: a dashed ring, as a status has always been
  },
  {
    to: '/communities',
    label: 'Communities',
    icon: null
  },
  {
    to: '/calls',
    label: 'Calls',
    icon: 'M6.6 3h3l1.5 4-2.2 1.6a12 12 0 0 0 5.5 5.5L16 11.9l4 1.5v3a2 2 0 0 1-2.2 2A16.5 16.5 0 0 1 4.6 5.2 2 2 0 0 1 6.6 3Z'
  }
]

function RailIcon ({ to }) {
  if (to === '/updates') {
    return (
      <>
        <circle cx="12" cy="12" r="8.4" stroke="currentColor" strokeWidth="1.8"
          strokeDasharray="4.2 2.6" />
        <circle cx="12" cy="12" r="3.2" stroke="currentColor" strokeWidth="1.8" />
      </>
    )
  }
  if (to === '/communities') {
    return (
      <>
        <path d="M4 19v-1.6a3.4 3.4 0 0 1 3.4-3.4h2.2a3.4 3.4 0 0 1 3.4 3.4V19"
          stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        <circle cx="8.5" cy="8.6" r="3" stroke="currentColor" strokeWidth="1.8" />
        <path d="M16 6.2a3 3 0 0 1 0 5.6M17 14.2c2 .4 3.4 2 3.4 4V19"
          stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </>
    )
  }
  const d = RAIL.find(t => t.to === to)?.icon
  return <path d={d} stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
}

export default function AppShell () {
  const { totalUnread } = useChats()

  return (
    <div className="shell">
      {/* Desktop only. Hidden below 1024px, where BottomNav takes over. */}
      <nav className="shell__rail" aria-label="Main">
        <div className="shell__rail-top">
          {RAIL.map(tab => (
            <NavLink
              key={tab.to}
              to={tab.to}
              className={({ isActive }) => `shell__rail-tab${isActive ? ' is-active' : ''}`}
              title={tab.label}
              aria-label={tab.label}
            >
              <span className="shell__rail-icon">
                <svg viewBox="0 0 24 24" fill="none"><RailIcon to={tab.to} /></svg>
                {tab.to === '/chats' && totalUnread > 0 && (
                  <span className="shell__rail-badge">
                    {totalUnread > 99 ? '99+' : totalUnread}
                  </span>
                )}
              </span>
            </NavLink>
          ))}
        </div>

        <div className="shell__rail-bottom">
          <NavLink to="/settings" className="shell__rail-tab" title="Settings" aria-label="Settings">
            <span className="shell__rail-icon">
              <svg viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="3.2" stroke="currentColor" strokeWidth="1.8" />
                <path d="M19.4 14.6a1.6 1.6 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.6 1.6 0 0 0-1.8-.3 1.6 1.6 0 0 0-1 1.5v.2a2 2 0 0 1-4 0v-.1a1.6 1.6 0 0 0-1-1.5 1.6 1.6 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.6 1.6 0 0 0 .3-1.8 1.6 1.6 0 0 0-1.5-1H3a2 2 0 0 1 0-4h.1a1.6 1.6 0 0 0 1.5-1 1.6 1.6 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.6 1.6 0 0 0 1.8.3h.1a1.6 1.6 0 0 0 1-1.5V3a2 2 0 0 1 4 0v.1a1.6 1.6 0 0 0 1 1.5 1.6 1.6 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.6 1.6 0 0 0-.3 1.8v.1a1.6 1.6 0 0 0 1.5 1h.2a2 2 0 0 1 0 4h-.1a1.6 1.6 0 0 0-1.5 1Z"
                  stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
              </svg>
            </span>
          </NavLink>

          <NavLink to="/profile" className="shell__rail-me" title="Profile" aria-label="Profile">
            <span className="shell__rail-avatar" />
          </NavLink>
        </div>
      </nav>

      <div className="shell__body">
        <Outlet />
      </div>

      {/* Phone only. Hidden from 1024px, where the rail takes over. */}
      <BottomNav />
    </div>
  )
}
